import React, { useEffect, useState } from "react";
import { Boxes, RefreshCw, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const NETWORK_LABEL = {
  facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok", linkedin: "LinkedIn",
  youtube: "YouTube", twitter: "X (Twitter)", threads: "Threads", bluesky: "Bluesky", pinterest: "Pinterest",
};

const BlotatoCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/blotato/status");
      setStatus(data);
    } catch { /* non-fatal */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/blotato/sync");
      toast.success(`Synced ${Object.keys(data.connected || {}).length} networks from Blotato.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't sync Blotato.");
    } finally { setSyncing(false); }
  };

  const selectAccount = async (platform, accountId) => {
    try {
      await api.put("/blotato/select", { platform, accountId });
      toast.success(`${NETWORK_LABEL[platform] || platform} account updated.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch account.");
    }
  };

  const connected = status?.connected || {};
  const options = status?.options || {};
  const connectedKeys = Object.keys(connected);

  return (
    <div className="mt-10 pt-8 border-t border-white/10" data-testid="blotato-section">
      <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.3em] mb-2">
        <Boxes size={13} /> Blotato — post to any network
      </div>
      {loading ? (
        <Loader2 className="animate-spin text-slate-500" size={18} />
      ) : !status?.enabled ? (
        <p className="text-sm text-slate-400 max-w-2xl" data-testid="blotato-not-connected">
          Blotato lets you publish to Facebook, Instagram, TikTok, LinkedIn, YouTube, X, Threads, Bluesky & Pinterest
          through one connection. It&apos;s not switched on yet — add your <span className="text-slate-200">Blotato API key</span>{" "}
          (from your Blotato dashboard → Settings → API) and it&apos;ll become your unified publisher across every ClydeFlow tool.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-400 max-w-2xl">
            Connected. Click Sync after linking accounts in your Blotato dashboard. These networks then appear as post targets everywhere.
          </p>
          <div className="flex flex-wrap gap-2">
            {(status.supported || []).map((n) => {
              const on = connectedKeys.includes(n);
              return (
                <span key={n} data-testid={`blotato-net-${n}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs border ${on ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200" : "border-white/10 text-slate-500"}`}>
                  {on && <Check size={12} />} {NETWORK_LABEL[n] || n}
                  {on && connected[n]?.username ? ` · ${connected[n].username}` : ""}
                </span>
              );
            })}
          </div>

          {/* Per-network account picker (only where >1 account exists) */}
          {connectedKeys.filter((n) => (options[n] || []).length > 1).length > 0 && (
            <div className="space-y-2 pt-1" data-testid="blotato-pickers">
              <p className="text-[11px] font-mono uppercase tracking-widest text-amber-300">
                You have multiple accounts on some networks — pick which one posts:
              </p>
              {connectedKeys.filter((n) => (options[n] || []).length > 1).map((n) => (
                <div key={n} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-24">{NETWORK_LABEL[n] || n}</span>
                  <select
                    data-testid={`blotato-select-${n}`}
                    value={connected[n]?.accountId || ""}
                    onChange={(e) => selectAccount(n, e.target.value)}
                    className="px-2 py-1.5 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-xs rounded-sm"
                  >
                    {(options[n] || []).map((o) => (
                      <option key={o.accountId} value={o.accountId}>
                        {o.username}{o.pageName ? ` · ${o.pageName}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          <button onClick={sync} disabled={syncing} data-testid="blotato-sync"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-widest hover:bg-[#33F3FF] disabled:opacity-50">
            {syncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Sync accounts
          </button>
        </div>
      )}
    </div>
  );
};

export default BlotatoCard;
